// ─────────────────────────────────────────────────────────────────────────────
// comparisonTool.js — Module Comparison Tool modal content
// ─────────────────────────────────────────────────────────────────────────────
//
// Rendered inside the shared Modal component (size="large").
// Receives `module` as a prop from moduleMetricsPage.js.
//
// All data fetching and tab state is managed by useComparisonTool().
// This file is purely presentational — it maps hook output to UI.
//
// Tab rendering is driven by COMPARISON_TABS from the hook so the tab list
// and its display labels have a single source of truth.
//
// CHANGES FROM PREVIOUS VERSION
// ──────────────────────────────

import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  useComparisonTool,
  COMPARISON_TABS,
} from "../../../hooks/useComparisonTool";
import { fetchAvailableModules } from "../../../../utils/dataReceiver";
import "../styles/comparisonTool.css";

// ─── FORMAT HELPERS ───────────────────────────────────────────────────────────

/** Decimal hours → "Xd Yh" string.  Returns "—" for null/NaN. */
function fmtHours(h) {
  if (h == null || Number.isNaN(h)) return "—";
  const d = Math.floor(h / 24);
  const r = Math.round(h % 24);
  return d > 0 ? `${d}d ${r}h` : `${r}h`;
}

/** ISO / YYYY-MM-DD date string → "Mon D, YYYY".  Returns "—" for null. */
function fmtDate(str) {
  if (!str) return "—";
  const d = new Date(str);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
}

/** Number → "±N.Xunit".  Returns "—" for null. */
function fmtDelta(n, unit = "", dec = 1) {
  if (n == null) return "—";
  return `${n > 0 ? "+" : ""}${n.toFixed(dec)}${unit}`;
}

/** Safe NC rate: (jobsRedone / totalTasks) * 100.  Returns null if totalTasks is 0. */
function ncRate(jobsRedone, totalTasks) {
  if (!totalTasks) return null;
  return (jobsRedone / totalTasks) * 100;
}

/**
 * Maps a JobStatus value (RTLS_ModuleMetricsActivityHistory) to one of the
 * existing Tag colour variants — no new CSS variants needed, these four
 * already exist for other tabs:
 *   "Ended"                    → "done"    (green)
 *   "Started"                  → "active"  (blue)
 *   "Interrupted"               → "late"    (red)
 *   "Coming" (not yet started) → "pending" (grey, outlined)
 *   anything else (unexpected) → "neutral" (grey)
 */
function jobStatusVariant(status) {
  switch (status) {
    case "Ended":
      return "done";
    case "Started":
      return "active";
    case "Interrupted":
      return "late";
    case "Coming":
      return "pending";
    default:
      return "neutral";
  }
}

/**
 * JobRow date + time → "Mon D, h:mm AM/PM" for display.
 * dateStr is the ISO-8601 RealizedStartDate/EndDate; timeStr is the raw
 * nvarchar RealizedStartTime/EndTime (e.g. "08:34 AM") — SQL stores them as
 * separate columns, so they're combined here rather than in dataServe.
 * Returns "—" when dateStr is null (job hasn't started/ended yet).
 */
function fmtJobDateTime(dateStr, timeStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  const datePart = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return timeStr ? `${datePart}, ${timeStr}` : datePart;
}

/** Comma-joined worker display names for a JobRow.workers array. */
function fmtWorkers(workers) {
  if (!workers || workers.length === 0) return "Unassigned";
  return workers
    .map((w) => w.employeeName || w.employeeId || "Unknown")
    .join(", ");
}

// ─── REUSABLE MICRO-COMPONENTS ────────────────────────────────────────────────

/**
 * KpiCard — one metric tile in a KPI row.
 * delta > 0 is green when betterDirection="higher", red when "lower".
 */
function KpiCard({
  label,
  value,
  subLabel,
  delta = null,
  deltaLabel = "",
  betterDirection = "higher",
}) {
  const isGood =
    delta == null
      ? null
      : betterDirection === "higher"
        ? delta >= 0
        : delta <= 0;

  return (
    <div className="mm-comp-kpi">
      <span className="mm-comp-kpi-label">{label}</span>
      <span className="mm-comp-kpi-value">{value ?? "—"}</span>
      {subLabel && <span className="mm-comp-kpi-sublabel">{subLabel}</span>}
      {delta != null && (
        <span
          className={`mm-comp-kpi-delta mm-comp-kpi-delta--${isGood ? "pos" : "neg"}`}
        >
          {delta > 0 ? "↑" : "↓"} {Math.abs(delta).toFixed(1)} {deltaLabel}
        </span>
      )}
    </div>
  );
}

/** Shimmer placeholder shown while a tab's data is loading. */
function LoadingSkeleton({ cards = 4 }) {
  return (
    <div className="mm-comp-skeleton">
      <div
        className="mm-comp-skeleton-kpi-row"
        style={{ gridTemplateColumns: `repeat(${cards}, 1fr)` }}
      >
        {Array.from({ length: cards }).map((_, i) => (
          <div key={i} className="mm-comp-skeleton-card" />
        ))}
      </div>
      <div className="mm-comp-skeleton-chart" />
      <div className="mm-comp-skeleton-table" />
    </div>
  );
}

/** Error banner with structured message and a Retry button. */
function ErrorState({ error, onRetry }) {
  return (
    <div className="mm-comp-error">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <p className="mm-comp-error-message">
        {error?.message ?? "Failed to load comparison data."}
      </p>
      {error?.code === "timeout" && (
        <p className="mm-comp-error-detail">
          The quality and job-completion views run multiple queries and can be
          slow on large datasets. Try again or contact your administrator if
          this persists.
        </p>
      )}
      <button className="mm-comp-retry-btn" onClick={onRetry}>
        Try Again
      </button>
    </div>
  );
}

/** Sortable table column header — renders a direction chevron inline. */
function Th({ col, sortCol, sortDir, onSort, children }) {
  const active = sortCol === col;
  return (
    <th
      className={`mm-comp-th-sortable${active ? " mm-comp-th-sortable--active" : ""}`}
      onClick={() => onSort(col)}
    >
      {children}
      <span className="mm-comp-sort-icon">
        {active ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"}
      </span>
    </th>
  );
}

/** Coloured pill badge used for status and schedule variance. */
function Tag({ label, variant }) {
  return <span className={`mm-comp-tag mm-comp-tag--${variant}`}>{label}</span>;
}

/**
 * ViewModeSlider — two-position toggle rendered above the Head to Head tab's
 * content, switching between the existing metrics comparison and the new
 * job-completion comparison. Purely controlled: `mode` and `onChange` come
 * from the hook's scheduleViewMode / setScheduleViewMode.
 */
function ViewModeSlider({ mode, onChange }) {
  return (
    <div
      className="mm-comp-view-slider"
      role="tablist"
      aria-label="Head to Head view"
    >
      <span
        className="mm-comp-view-slider-thumb"
        style={{ transform: mode === "jobs" ? "translateX(100%)" : "none" }}
        aria-hidden="true"
      />
      <button
        type="button"
        role="tab"
        aria-selected={mode === "metrics"}
        className={`mm-comp-view-slider-btn${
          mode === "metrics" ? " mm-comp-view-slider-btn--active" : ""
        }`}
        onClick={() => onChange("metrics")}
      >
        Metrics Comparison
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === "jobs"}
        className={`mm-comp-view-slider-btn${
          mode === "jobs" ? " mm-comp-view-slider-btn--active" : ""
        }`}
        onClick={() => onChange("jobs")}
      >
        Job Completion
      </button>
    </div>
  );
}

// ─── PANEL A: Item Number Comparison (mode = "peer") ──────────────────────────
//
// Renamed from ConfigBenchmarkPanel.
// "Total Line Duration — Completed Peer Distribution" section removed.
// All other content (KPI row, peer table) unchanged.

function ItemNumberComparisonPanel({ data, module }) {
  const [sortCol, setSortCol] = useState("hoursOnLine");
  const [sortDir, setSortDir] = useState("asc");

  const { typeClass, thisModule: tm, peers, peerAverages: avg } = data;

  const allPeers = useMemo(
    () => [
      ...(peers?.inProgress ?? []).map((p) => ({
        ...p,
        _status: "In Progress",
      })),
      ...(peers?.completed ?? []).map((p) => ({ ...p, _status: "Completed" })),
    ],
    [peers],
  );

  const sortedPeers = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...allPeers].sort((a, b) => {
      const av = a[sortCol] ?? 0;
      const bv = b[sortCol] ?? 0;
      return av > bv ? dir : av < bv ? -dir : 0;
    });
  }, [allPeers, sortCol, sortDir]);

  const onSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const thisNCRate = ncRate(tm?.jobsRedone, tm?.totalTasks);

  return (
    <div className="mm-comp-panel">
      <p className="modal-description">
        Comparing <strong>{module.workOrder}</strong> against{" "}
        <strong>{allPeers.length}</strong> other <strong>{typeClass}</strong>{" "}
        module{allPeers.length !== 1 ? "s" : ""} (
        {(peers?.inProgress ?? []).length} in&nbsp;progress,{" "}
        {(peers?.completed ?? []).length} completed).
      </p>

      {/* KPI row */}
      <div className="mm-comp-kpi-row">
        <KpiCard
          label="Time on Line"
          value={fmtHours(tm?.hoursOnLine ?? 0)}
          subLabel={`Avg: ${fmtHours(avg?.hoursOnLine)}`}
          delta={
            avg?.hoursOnLine != null
              ? +((tm?.hoursOnLine ?? 0) - avg.hoursOnLine).toFixed(1)
              : null
          }
          deltaLabel="hrs vs avg"
          betterDirection="lower"
        />
        <KpiCard
          label="Hrs / Sqft"
          value={tm?.hoursPerSqft ? tm.hoursPerSqft.toFixed(3) : "—"}
          subLabel={`Avg: ${avg?.hoursPerSqft != null ? avg.hoursPerSqft.toFixed(3) : "—"}`}
          delta={
            avg?.hoursPerSqft != null && tm?.hoursPerSqft
              ? +(tm.hoursPerSqft - avg.hoursPerSqft).toFixed(3)
              : null
          }
          deltaLabel="vs avg"
          betterDirection="lower"
        />
        <KpiCard
          label="Completion Rate"
          value={
            tm?.completionRate != null
              ? `${tm.completionRate.toFixed(1)}%`
              : "—"
          }
          subLabel={`Avg: ${avg?.completionRate != null ? avg.completionRate.toFixed(1) + "%" : "—"}`}
          delta={
            avg?.completionRate != null && tm?.completionRate != null
              ? +(tm.completionRate - avg.completionRate).toFixed(1)
              : null
          }
          deltaLabel="% vs avg"
          betterDirection="higher"
        />
        <KpiCard
          label="NC Rate"
          value={thisNCRate != null ? `${thisNCRate.toFixed(1)}%` : "—"}
          subLabel={`Avg: ${avg?.ncRate != null ? avg.ncRate.toFixed(1) + "%" : "—"}`}
          delta={
            avg?.ncRate != null && thisNCRate != null
              ? +(thisNCRate - avg.ncRate).toFixed(1)
              : null
          }
          deltaLabel="% vs avg"
          betterDirection="lower"
        />
        <KpiCard
          label="Peer Set"
          value={allPeers.length}
          subLabel={`Type class: ${typeClass}`}
        />
      </div>

      {/* "Total Line Duration — Completed Peer Distribution" section removed */}

      {/* Peer table */}
      <div className="modal-section modal-section--scroll">
        <h3>All Type-Class Peers</h3>
        <div className="modal-table-scroll">
          <table className="modal-table">
            <thead>
              <tr>
                <th>WO #</th>
                <th>Property</th>
                <th>Status</th>
                <Th
                  col="hoursOnLine"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={onSort}
                >
                  Time on Line
                </Th>
                <Th
                  col="hoursPerSqft"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={onSort}
                >
                  Hrs / Sqft
                </Th>
                <Th
                  col="completionRate"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={onSort}
                >
                  Completion
                </Th>
                <Th
                  col="jobsRedone"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={onSort}
                >
                  NCs
                </Th>
              </tr>
            </thead>
            <tbody>
              {sortedPeers.map((p) => {
                const isThis = p.woNum === module.workOrder;
                const v = p.scheduleVarianceDays;
                const schedLabel =
                  v == null
                    ? null
                    : v <= 0
                      ? `${Math.abs(v)}d early`
                      : `${v}d late`;
                const schedVar =
                  v == null ? "neutral" : v <= 0 ? "on-time" : "late";
                return (
                  <tr
                    key={p.woNum}
                    className={isThis ? "mm-comp-row--this" : ""}
                  >
                    <td>
                      {p.woNum}
                      {isThis && (
                        <span className="mm-comp-this-badge"> ← This</span>
                      )}
                    </td>
                    <td>{p.property ?? "—"}</td>
                    <td>
                      <Tag
                        label={p._status}
                        variant={p._status === "Completed" ? "done" : "active"}
                      />
                    </td>
                    <td>
                      {fmtHours(
                        p._status === "Completed"
                          ? p.totalHoursOnLine
                          : p.hoursOnLine,
                      )}
                    </td>
                    <td>{p.hoursPerSqft ? p.hoursPerSqft.toFixed(3) : "—"}</td>
                    <td>
                      {p._status === "Completed"
                        ? "100%"
                        : p.completionRate != null
                          ? `${p.completionRate.toFixed(1)}%`
                          : "—"}
                    </td>
                    <td>
                      {p._status === "In Progress"
                        ? (p.jobsRedone ?? "—")
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── PANEL B: Project Progress (mode = "project") ─────────────────────────────
// Unchanged from previous version.

function ProjectProgressPanel({ data, module }) {
  const { property, totals, scheduled, inProgress, completed, avgNcPerModule } =
    data;

  const completedCount = completed?.length ?? 0;
  const inProgressCount = inProgress?.length ?? 0;
  const totalModules = totals?.totalModules ?? 0;
  const totalSqft = totals?.totalSqft ?? 0;
  const notStartedCount = Math.max(
    0,
    totalModules - completedCount - inProgressCount,
  );
  const completedSqft = (completed ?? []).reduce(
    (s, m) => s + (m.sqft ?? 0),
    0,
  );
  const completionPct =
    totalModules > 0 ? ((completedCount / totalModules) * 100).toFixed(1) : "0";
  const sqftPct =
    totalSqft > 0 ? ((completedSqft / totalSqft) * 100).toFixed(1) : "0";
  const onTimeCount = (completed ?? []).filter(
    (m) => (m.scheduleVarianceDays ?? 0) <= 0,
  ).length;
  const onTimeRate =
    completedCount > 0
      ? ((onTimeCount / completedCount) * 100).toFixed(1)
      : null;

  const maxStation = Math.min(
    20,
    Math.max(...(inProgress ?? []).map((m) => m.currentStation ?? 0), 1),
  );
  const stationNums = Array.from({ length: maxStation }, (_, i) => i + 1);
  const activeWoNums = new Set((inProgress ?? []).map((m) => m.woNum));
  const completedWoNums = new Set((completed ?? []).map((m) => m.woNum));

  return (
    <div className="mm-comp-panel">
      <p className="modal-description">
        All <strong>{totalModules}</strong> modules scheduled for{" "}
        <strong>{property}</strong> — {completedCount} complete,{" "}
        {inProgressCount} in&nbsp;progress, {notStartedCount} not yet started.
      </p>

      <div className="mm-comp-kpi-row">
        <KpiCard
          label="Project Completion"
          value={`${completionPct}%`}
          subLabel={`${completedCount} of ${totalModules} modules`}
        />
        <KpiCard
          label="Sqft Delivered"
          value={`${sqftPct}%`}
          subLabel={`${completedSqft.toLocaleString()} of ${totalSqft.toLocaleString()} sqft`}
        />
        <KpiCard
          label="On-Time Rate"
          value={onTimeRate != null ? `${onTimeRate}%` : "—"}
          subLabel={`${onTimeCount} of ${completedCount} on time`}
          betterDirection="higher"
        />
        <KpiCard
          label="Avg NC / Module"
          value={avgNcPerModule != null ? avgNcPerModule.toFixed(1) : "—"}
          subLabel="in-progress modules"
          betterDirection="lower"
        />
      </div>

      <div className="modal-section">
        <h3>Project Pipeline</h3>
        <div className="mm-comp-funnel">
          {[
            {
              label: "Not Started",
              count: notStartedCount,
              variant: "pending",
            },
            { label: "In Progress", count: inProgressCount, variant: "active" },
            { label: "Completed", count: completedCount, variant: "done" },
          ].map((seg) => (
            <div
              key={seg.label}
              className={`mm-comp-funnel-seg mm-comp-funnel-seg--${seg.variant}`}
            >
              <span className="mm-comp-funnel-count">{seg.count}</span>
              <span className="mm-comp-funnel-label">{seg.label}</span>
            </div>
          ))}
        </div>
      </div>

      {inProgressCount > 0 && (
        <div className="modal-section">
          <h3>In-Progress Module Stations</h3>
          <div className="mm-comp-ladder">
            <div className="mm-comp-ladder-row">
              <div className="mm-comp-ladder-label" />
              {stationNums.map((s) => (
                <div
                  key={s}
                  className="mm-comp-ladder-cell mm-comp-ladder-cell--header"
                >
                  {s}
                </div>
              ))}
            </div>
            {(inProgress ?? []).map((m) => {
              const isThis = m.woNum === module.workOrder;
              return (
                <div
                  key={m.woNum}
                  className={`mm-comp-ladder-row${isThis ? " mm-comp-ladder-row--highlight" : ""}`}
                >
                  <div className="mm-comp-ladder-label" title={m.woNum}>
                    {isThis ? "→ " : ""}
                    {m.woNum}
                  </div>
                  {stationNums.map((s) => {
                    const cur = m.currentStation ?? 0;
                    const cls =
                      s < cur
                        ? "mm-comp-ladder-cell--done"
                        : s === cur
                          ? "mm-comp-ladder-cell--current"
                          : "mm-comp-ladder-cell--upcoming";
                    return (
                      <div
                        key={s}
                        className={`mm-comp-ladder-cell ${cls}`}
                        title={`${m.woNum} — Station ${s}`}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="modal-section modal-section--scroll">
        <h3>All Project Modules</h3>
        <div className="modal-table-scroll">
          <table className="modal-table">
            <thead>
              <tr>
                <th>WO #</th>
                <th>Item Type</th>
                <th>Status</th>
                <th>Station</th>
                <th>Sqft</th>
                <th>Completion</th>
                <th>NCs</th>
                <th>Sched Offline</th>
                <th>Variance</th>
              </tr>
            </thead>
            <tbody>
              {(scheduled ?? [])
                .filter(
                  (s) =>
                    !activeWoNums.has(s.woNum) && !completedWoNums.has(s.woNum),
                )
                .map((s) => (
                  <tr key={s.woNum} className="mm-comp-row--pending">
                    <td>{s.woNum}</td>
                    <td>{s.itemNum}</td>
                    <td>
                      <Tag label="Not Started" variant="pending" />
                    </td>
                    <td>—</td>
                    <td>{s.sqft?.toLocaleString() ?? "—"}</td>
                    <td>—</td>
                    <td>—</td>
                    <td>{fmtDate(s.scheduledOfflineDate)}</td>
                    <td>—</td>
                  </tr>
                ))}
              {(inProgress ?? []).map((m) => {
                const isThis = m.woNum === module.workOrder;
                return (
                  <tr
                    key={m.woNum}
                    className={isThis ? "mm-comp-row--this" : ""}
                  >
                    <td>
                      {m.woNum}
                      {isThis && (
                        <span className="mm-comp-this-badge"> ← This</span>
                      )}
                    </td>
                    <td>{m.itemNum}</td>
                    <td>
                      <Tag label="In Progress" variant="active" />
                    </td>
                    <td>Stn {m.currentStation ?? "—"}</td>
                    <td>{m.sqft?.toLocaleString() ?? "—"}</td>
                    <td>
                      {m.completionRate != null
                        ? `${m.completionRate.toFixed(1)}%`
                        : "—"}
                    </td>
                    <td>{m.jobsRedone ?? "—"}</td>
                    <td>{fmtDate(m.scheduledOfflineDate)}</td>
                    <td>—</td>
                  </tr>
                );
              })}
              {(completed ?? []).map((m) => {
                const v = m.scheduleVarianceDays;
                return (
                  <tr key={m.woNum}>
                    <td>{m.woNum}</td>
                    <td>{m.itemNum}</td>
                    <td>
                      <Tag label="Completed" variant="done" />
                    </td>
                    <td>—</td>
                    <td>{m.sqft?.toLocaleString() ?? "—"}</td>
                    <td>100%</td>
                    <td>—</td>
                    <td>{fmtDate(m.scheduledOfflineDate)}</td>
                    <td>
                      {v != null ? (
                        <Tag
                          label={
                            v <= 0 ? `${Math.abs(v)}d early` : `${v}d late`
                          }
                          variant={v <= 0 ? "on-time" : "late"}
                        />
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── PANEL C: Head to Head Comparison (mode = "schedule") ─────────────────────
//
// Replaces ScheduleRiskPanel entirely.
//
// Renders two ModuleSnapshot columns side by side — thisModule on the left,
// peerModule on the right — with a delta column in between for each metric.
// The peer selector dropdown lets the user switch the comparison target;
// calling selectPeer() from the hook aborts the current fetch and re-fetches
// with the new peer WO#.
//
// When peerModule is null (no same-typeClass peers exist in currentOnLineMods)
// a notice is shown instead of the comparison table.

/** One column in the head-to-head layout — label stack for a single module. */
function H2HColumn({ label, isCurrent, children }) {
  return (
    <div
      className={`mm-comp-h2h-col${isCurrent ? " mm-comp-h2h-col--current" : ""}`}
    >
      <div className="mm-comp-h2h-col-label">{label}</div>
      {children}
    </div>
  );
}

/** One metric row spanning both columns + a delta cell. */
function H2HRow({ metric, aValue, bValue, delta, deltaGoodDir = "higher" }) {
  const isGood =
    delta == null ? null : deltaGoodDir === "higher" ? delta >= 0 : delta <= 0;

  return (
    <div className="mm-comp-h2h-row">
      <div className="mm-comp-h2h-metric">{metric}</div>
      <div className="mm-comp-h2h-val mm-comp-h2h-val--a">{aValue}</div>
      <div
        className={`mm-comp-h2h-delta${
          delta == null
            ? ""
            : isGood
              ? " mm-comp-h2h-delta--pos"
              : " mm-comp-h2h-delta--neg"
        }`}
      >
        {delta == null
          ? "—"
          : `${delta > 0 ? "+" : ""}${typeof delta === "number" ? delta.toFixed(1) : delta}`}
      </div>
      <div className="mm-comp-h2h-val mm-comp-h2h-val--b">{bValue}</div>
    </div>
  );
}

/**
 * ModuleSearchBar — lets the user pick ANY module to compare against (not
 * just same-typeClass peers), pulling from the "available-modules" route via
 * fetchAvailableModules. Rendered once at the ComparisonTool root (not
 * per-view) so it works no matter which Head to Head view (metrics or jobs)
 * is currently showing — both call the same onSelect (the hook's selectPeer).
 *
 * The full module list is fetched once per mount and filtered client-side —
 * it's O(hundreds) of rows (see dataServe.js's available-modules handler
 * comments) so this is cheap and avoids a network round-trip per keystroke.
 */
function ModuleSearchBar({ excludeWoNum, onSelect }) {
  const [modules, setModules] = useState([]);
  const [fetchStatus, setFetchStatus] = useState("loading"); // loading | success | error
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Fetch the full available-modules list once on mount.
  useEffect(() => {
    const controller = new AbortController();
    fetchAvailableModules(controller.signal)
      .then((list) => {
        setModules(list);
        setFetchStatus("success");
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setFetchStatus("error");
      });
    return () => controller.abort();
  }, []);

  // Close the results dropdown on any click outside this component.
  useEffect(() => {
    function onDocMouseDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return modules
      .filter((m) => m.woNum !== excludeWoNum)
      .filter(
        (m) =>
          m.woNum.toLowerCase().includes(q) ||
          (m.itemNum ?? "").toLowerCase().includes(q) ||
          (m.batchNum ?? "").toLowerCase().includes(q),
      )
      .slice(0, 15); // cap the dropdown to a scrollable, readable length
  }, [modules, query, excludeWoNum]);

  const handleSelect = (m) => {
    onSelect(m.woNum);
    setQuery("");
    setIsOpen(false);
  };

  return (
    <div className="mm-comp-h2h-search" ref={containerRef}>
      <label className="mm-comp-h2h-selector-label" htmlFor="h2h-module-search">
        Compare against any module:
      </label>

      <div className="mm-comp-h2h-search-input-wrap">
        <svg
          className="mm-comp-h2h-search-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          id="h2h-module-search"
          type="text"
          className="mm-comp-h2h-search-input"
          placeholder="Search WO#, item #, or batch #…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
      </div>

      {isOpen && query.trim() && (
        <div className="mm-comp-h2h-search-results">
          {fetchStatus === "loading" ? (
            <div className="mm-comp-h2h-search-empty">Loading module list…</div>
          ) : fetchStatus === "error" ? (
            <div className="mm-comp-h2h-search-empty">
              Couldn't load the module list. Try again shortly.
            </div>
          ) : results.length === 0 ? (
            <div className="mm-comp-h2h-search-empty">
              No modules match "{query}".
            </div>
          ) : (
            results.map((m) => (
              <button
                key={m.woNum}
                type="button"
                className="mm-comp-h2h-search-result"
                onClick={() => handleSelect(m)}
              >
                <span className="mm-comp-h2h-search-result-wo">{m.woNum}</span>
                {m.itemNum && (
                  <span className="mm-comp-h2h-search-result-meta">
                    {m.itemNum}
                  </span>
                )}
                {m.batchNum && (
                  <span className="mm-comp-h2h-search-result-meta">
                    {m.batchNum}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function HeadToHeadPanel({ data, module, onSelectPeer }) {
  const {
    typeClass,
    thisModule: tm,
    peerModule: pm,
    peerWoNum,
    availablePeers,
  } = data;

  // No peers at all for this type class
  if (!pm) {
    return (
      <div className="mm-comp-panel">
        <p className="modal-description">
          Head to head comparison for <strong>{module.workOrder}</strong> ( type
          class <strong>{typeClass}</strong>).
        </p>
        <p className="mm-comp-empty">
          No other in-progress modules share the <strong>{typeClass}</strong>{" "}
          type class — head to head comparison is unavailable until a peer
          module comes online.
        </p>
      </div>
    );
  }

  // Deltas: thisModule value minus peerModule value
  const deltaHours =
    tm && pm ? +(tm.hoursOnLine - pm.hoursOnLine).toFixed(1) : null;
  const deltaCompletion =
    tm && pm ? +(tm.completionRate - pm.completionRate).toFixed(1) : null;
  const deltaHoursPerSqft =
    tm && pm && tm.hoursPerSqft != null && pm.hoursPerSqft != null
      ? +(tm.hoursPerSqft - pm.hoursPerSqft).toFixed(3)
      : null;
  const deltaMannedHours =
    tm && pm ? +(tm.totalMannedHours - pm.totalMannedHours).toFixed(1) : null;
  const deltaDaysOnLine =
    tm?.daysOnLine != null && pm?.daysOnLine != null
      ? tm.daysOnLine - pm.daysOnLine
      : null;
  const tmNcRate = ncRate(tm?.jobsRedone, tm?.totalTasks);
  const pmNcRate = ncRate(pm?.jobsRedone, pm?.totalTasks);
  const deltaNcRate =
    tmNcRate != null && pmNcRate != null
      ? +(tmNcRate - pmNcRate).toFixed(1)
      : null;

  return (
    <div className="mm-comp-panel">
      <p className="modal-description">
        Direct comparison of <strong>{module.workOrder}</strong> against{" "}
        <strong>{pm.woNum}</strong>
        {pm.itemNum ? ` (${pm.itemNum})` : ""}.
      </p>

      {/* Side-by-side detail comparison */}
      <div className="modal-section">
        <h3>Metric Comparison</h3>

        {/* Column headers */}
        <div className="mm-comp-h2h-header">
          <div className="mm-comp-h2h-metric" />
          <div className="mm-comp-h2h-col-header mm-comp-h2h-col-header--current">
            {module.workOrder}
            <span className="mm-comp-h2h-col-badge">This module</span>
          </div>
          <div className="mm-comp-h2h-col-header mm-comp-h2h-col-header--delta">
            Delta
          </div>
          <div className="mm-comp-h2h-col-header">
            {pm.woNum}
            <span className="mm-comp-h2h-col-badge">
              {pm.property ?? "Peer"}
            </span>
          </div>
        </div>

        <div className="mm-comp-h2h-table">
          <H2HRow
            metric="Item Number"
            aValue={tm?.itemNum ?? "—"}
            bValue={pm?.itemNum ?? "—"}
            delta={null}
          />
          <H2HRow
            metric="Property"
            aValue={tm?.property ?? "—"}
            bValue={pm?.property ?? "—"}
            delta={null}
          />
          <H2HRow
            metric="Sqft"
            aValue={tm?.sqft?.toLocaleString() ?? "—"}
            bValue={pm?.sqft?.toLocaleString() ?? "—"}
            delta={null}
          />
          <H2HRow
            metric="Online Date"
            aValue={fmtDate(tm?.actualOnlineDate)}
            bValue={fmtDate(pm?.actualOnlineDate)}
            delta={null}
          />
          <H2HRow
            metric="Sched Offline"
            aValue={fmtDate(tm?.scheduledOfflineDate)}
            bValue={fmtDate(pm?.scheduledOfflineDate)}
            delta={null}
          />
          <H2HRow
            metric="Days on Line"
            aValue={tm?.daysOnLine ?? "—"}
            bValue={pm?.daysOnLine ?? "—"}
            delta={deltaDaysOnLine}
            deltaGoodDir="lower"
          />
          <H2HRow
            metric="Days Remaining"
            aValue={tm?.daysRemaining ?? "—"}
            bValue={pm?.daysRemaining ?? "—"}
            delta={
              tm?.daysRemaining != null && pm?.daysRemaining != null
                ? tm.daysRemaining - pm.daysRemaining
                : null
            }
            deltaGoodDir="higher"
          />
          <H2HRow
            metric="Time on Line"
            aValue={fmtHours(tm?.hoursOnLine)}
            bValue={fmtHours(pm?.hoursOnLine)}
            delta={deltaHours}
            deltaGoodDir="lower"
          />
          <H2HRow
            metric="Completion Rate"
            aValue={
              tm?.completionRate != null
                ? `${tm.completionRate.toFixed(1)}%`
                : "—"
            }
            bValue={
              pm?.completionRate != null
                ? `${pm.completionRate.toFixed(1)}%`
                : "—"
            }
            delta={deltaCompletion}
            deltaGoodDir="higher"
          />
          <H2HRow
            metric="Manned Hours"
            aValue={
              tm?.totalMannedHours != null
                ? tm.totalMannedHours.toFixed(1)
                : "—"
            }
            bValue={
              pm?.totalMannedHours != null
                ? pm.totalMannedHours.toFixed(1)
                : "—"
            }
            delta={deltaMannedHours}
            deltaGoodDir="lower"
          />
          <H2HRow
            metric="Hrs / Sqft"
            aValue={tm?.hoursPerSqft ? tm.hoursPerSqft.toFixed(3) : "—"}
            bValue={pm?.hoursPerSqft ? pm.hoursPerSqft.toFixed(3) : "—"}
            delta={deltaHoursPerSqft}
            deltaGoodDir="lower"
          />
          <H2HRow
            metric="Jobs Redone"
            aValue={tm?.jobsRedone ?? "—"}
            bValue={pm?.jobsRedone ?? "—"}
            delta={
              tm?.jobsRedone != null && pm?.jobsRedone != null
                ? tm.jobsRedone - pm.jobsRedone
                : null
            }
            deltaGoodDir="lower"
          />
          <H2HRow
            metric="NC Rate"
            aValue={tmNcRate != null ? `${tmNcRate.toFixed(1)}%` : "—"}
            bValue={pmNcRate != null ? `${pmNcRate.toFixed(1)}%` : "—"}
            delta={deltaNcRate}
            deltaGoodDir="lower"
          />
        </div>
      </div>
    </div>
  );
}

// ─── PANEL C-2: Job Completion (internal mode = "jobs", Head to Head slider) ──
//
// Rendered instead of HeadToHeadPanel when scheduleViewMode === "jobs".
// data is a ComparisonJobsPayload from dataServe/dataReceiver's mode="jobs":
//   { mode, woNum, peerWoNum, thisJobs, peerJobs, thisSummary, peerSummary }
//
// Layout mirrors HeadToHeadPanel's "Metric Comparison" section (same
// mm-comp-h2h-* classes, same H2HRow component) for a summary comparison,
// then adds a new two-column "Job List" section with one sortable table per
// module (see JobTable below).
//
// The ModuleSearchBar (any-module search) and HeadToHeadPanel's quick
// same-typeClass dropdown both live at the ComparisonTool root / inside
// HeadToHeadPanel respectively — neither is duplicated here. Both call the
// same onSelectPeer, which works no matter which of the two views (metrics
// or jobs) is currently active (see useComparisonTool.js).

/**
 * JobTable — one module's sortable job list, used side-by-side (this module /
 * peer module) inside JobCompletionPanel.
 *
 * Sorting is local to this instance — sorting "this module"'s table does not
 * affect the peer's table, since the two job lists are independent and
 * usually different lengths/orders (jobs don't correspond 1:1 across modules).
 */
function JobTable({ label, jobs, isCurrent }) {
  const [sortCol, setSortCol] = useState("startDate");
  const [sortDir, setSortDir] = useState("asc");

  const sortedJobs = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...jobs].sort((a, b) => {
      let av = a[sortCol];
      let bv = b[sortCol];
      if (sortCol === "startDate" || sortCol === "endDate") {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      } else {
        av = av ?? "";
        bv = bv ?? "";
      }
      return av > bv ? dir : av < bv ? -dir : 0;
    });
  }, [jobs, sortCol, sortDir]);

  const onSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  return (
    <div
      className={`mm-comp-jobs-column${isCurrent ? " mm-comp-jobs-column--current" : ""}`}
    >
      <div className="mm-comp-jobs-column-header">
        {label}
        {isCurrent && (
          <span className="mm-comp-h2h-col-badge">This module</span>
        )}
        <span className="mm-comp-section-count"> ({jobs.length})</span>
      </div>

      {jobs.length === 0 ? (
        <p className="mm-comp-empty">No jobs recorded for this module.</p>
      ) : (
        <div className="mm-comp-jobs-table-scroll">
          <table className="modal-table">
            <thead>
              <tr>
                <Th
                  col="jobId"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={onSort}
                >
                  Job ID
                </Th>
                <th>Op #</th>
                <th>Status</th>
                <Th
                  col="startDate"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={onSort}
                >
                  Start
                </Th>
                <th>End</th>
                <Th
                  col="hours"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={onSort}
                >
                  Hours
                </Th>
                <th>Worker(s)</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs.map((j, i) => (
                <tr key={j.jobId ?? `${label}-${i}`}>
                  <td>{j.jobId ?? "—"}</td>
                  <td>{j.operationNumber ?? "—"}</td>
                  <td>
                    <Tag
                      label={j.jobStatus ?? "Unknown"}
                      variant={jobStatusVariant(j.jobStatus)}
                    />
                  </td>
                  <td>{fmtJobDateTime(j.startDate, j.startTime)}</td>
                  <td>{fmtJobDateTime(j.endDate, j.endTime)}</td>
                  <td>{j.hours != null ? j.hours.toFixed(2) : "—"}</td>
                  <td
                    className="mm-comp-jobs-workers-cell"
                    title={fmtWorkers(j.workers)}
                  >
                    {fmtWorkers(j.workers)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function JobCompletionPanel({ data, module }) {
  const {
    peerWoNum,
    thisJobs = [],
    peerJobs = [],
    thisSummary,
    peerSummary,
  } = data;

  // Deltas: thisModule value minus peerModule value — same convention as
  // HeadToHeadPanel's metric deltas.
  const deltaTotalJobs = thisSummary.totalJobs - peerSummary.totalJobs;
  const deltaCompletedJobs =
    thisSummary.completedJobs - peerSummary.completedJobs;
  const deltaCompletion = +(
    thisSummary.completionRate - peerSummary.completionRate
  ).toFixed(1);
  const deltaHours = +(thisSummary.totalHours - peerSummary.totalHours).toFixed(
    2,
  );
  const deltaAvgHours = +(
    thisSummary.avgHoursPerJob - peerSummary.avgHoursPerJob
  ).toFixed(2);
  const deltaWorkers =
    thisSummary.uniqueWorkerCount - peerSummary.uniqueWorkerCount;

  return (
    <div className="mm-comp-panel">
      <p className="modal-description">
        Job-by-job comparison of <strong>{module.workOrder}</strong> against{" "}
        <strong>{peerWoNum}</strong>. Use the search bar above to compare
        against a different module.
      </p>

      {/* Summary comparison — same H2H layout/components as the metrics view */}
      <div className="modal-section">
        <h3>Job Completion Summary</h3>

        <div className="mm-comp-h2h-header">
          <div className="mm-comp-h2h-metric" />
          <div className="mm-comp-h2h-col-header mm-comp-h2h-col-header--current">
            {module.workOrder}
            <span className="mm-comp-h2h-col-badge">This module</span>
          </div>
          <div className="mm-comp-h2h-col-header mm-comp-h2h-col-header--delta">
            Delta
          </div>
          <div className="mm-comp-h2h-col-header">
            {peerWoNum}
            <span className="mm-comp-h2h-col-badge">Peer</span>
          </div>
        </div>

        <div className="mm-comp-h2h-table">
          <H2HRow
            metric="Total Jobs"
            aValue={thisSummary.totalJobs}
            bValue={peerSummary.totalJobs}
            delta={deltaTotalJobs}
            deltaGoodDir="higher"
          />
          <H2HRow
            metric="Completed"
            aValue={thisSummary.completedJobs}
            bValue={peerSummary.completedJobs}
            delta={deltaCompletedJobs}
            deltaGoodDir="higher"
          />
          <H2HRow
            metric="In Progress"
            aValue={thisSummary.inProgressJobs}
            bValue={peerSummary.inProgressJobs}
            delta={null}
          />
          <H2HRow
            metric="Pending"
            aValue={thisSummary.pendingJobs}
            bValue={peerSummary.pendingJobs}
            delta={null}
          />
          <H2HRow
            metric="Completion Rate"
            aValue={`${thisSummary.completionRate.toFixed(1)}%`}
            bValue={`${peerSummary.completionRate.toFixed(1)}%`}
            delta={deltaCompletion}
            deltaGoodDir="higher"
          />
          <H2HRow
            metric="Total Hours"
            aValue={thisSummary.totalHours.toFixed(1)}
            bValue={peerSummary.totalHours.toFixed(1)}
            delta={deltaHours}
            deltaGoodDir="lower"
          />
          <H2HRow
            metric="Avg Hrs / Job"
            aValue={thisSummary.avgHoursPerJob.toFixed(2)}
            bValue={peerSummary.avgHoursPerJob.toFixed(2)}
            delta={deltaAvgHours}
            deltaGoodDir="lower"
          />
          <H2HRow
            metric="Unique Workers"
            aValue={thisSummary.uniqueWorkerCount}
            bValue={peerSummary.uniqueWorkerCount}
            delta={deltaWorkers}
            deltaGoodDir="higher"
          />
        </div>
      </div>

      {/* Per-job detail — one independently-sortable table per module */}
      <div className="modal-section">
        <h3>Job List</h3>
        <div className="mm-comp-jobs-columns">
          <JobTable label={module.workOrder} jobs={thisJobs} isCurrent />
          <JobTable label={peerWoNum} jobs={peerJobs} />
        </div>
      </div>
    </div>
  );
}

// ─── PANEL D: Quality Analysis (mode = "quality") ─────────────────────────────
//
// "Station NC Heatmap" section removed.
// The excluded transit reason is filtered server-side — no frontend logic needed.

function QualityPanel({ data, module }) {
  const [sortCol, setSortCol] = useState("stationId");
  const [sortDir, setSortDir] = useState("asc");

  const {
    thisModule: tm,
    ncDetail,
    reasonBreakdown,
    alertStatus,
    fprGoal,
    peerAvgNcRate,
  } = data;

  // fprGoal stored as 1.0 = 100% in RTLS_ProductionGoals
  const fprGoalPct = fprGoal != null ? fprGoal * 100 : null;
  const passedTasks = (tm?.totalTasks ?? 0) - (tm?.jobsRedone ?? 0);
  const fpr = ncRate(passedTasks, tm?.totalTasks);
  const fprDelta =
    fpr != null && fprGoalPct != null ? +(fpr - fprGoalPct).toFixed(1) : null;
  const thisNCRate = ncRate(tm?.jobsRedone, tm?.totalTasks);

  const maxFreq = Math.max(
    ...(reasonBreakdown ?? []).map((r) => r.frequency),
    1,
  );

  const onSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const sortedEvents = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...(ncDetail ?? [])].sort((a, b) => {
      const av = a[sortCol] ?? "";
      const bv = b[sortCol] ?? "";
      return av > bv ? dir : av < bv ? -dir : 0;
    });
  }, [ncDetail, sortCol, sortDir]);

  return (
    <div className="mm-comp-panel">
      <p className="modal-description">
        Non-conformance breakdown for <strong>{module.workOrder}</strong> —
        reason distribution and comparison to the{" "}
        <strong>{data.typeClass}</strong> peer average.
      </p>

      {/* KPI row */}
      <div className="mm-comp-kpi-row">
        <KpiCard
          label="Jobs Redone"
          value={tm?.jobsRedone ?? "—"}
          subLabel={`of ${tm?.totalTasks ?? "—"} tasks`}
          betterDirection="lower"
        />
        <KpiCard
          label="First Pass Rate"
          value={fpr != null ? `${fpr.toFixed(1)}%` : "—"}
          subLabel={
            fprGoalPct != null ? `Goal: ${fprGoalPct.toFixed(0)}%` : undefined
          }
          delta={fprDelta}
          deltaLabel="% vs goal"
          betterDirection="higher"
        />
        <KpiCard
          label="Peer Avg NC Rate"
          value={peerAvgNcRate != null ? `${peerAvgNcRate.toFixed(1)}%` : "—"}
          subLabel="same type class"
          delta={
            thisNCRate != null && peerAvgNcRate != null
              ? +(thisNCRate - peerAvgNcRate).toFixed(1)
              : null
          }
          deltaLabel="% vs peers"
          betterDirection="lower"
        />
        <KpiCard
          label="Open Alerts"
          value={alertStatus?.openAlerts ?? "—"}
          subLabel={`${alertStatus?.closedAlerts ?? "—"} closed`}
          betterDirection="lower"
        />
      </div>

      {/* Station NC Heatmap section removed */}

      {/* NC reason breakdown */}
      {(reasonBreakdown ?? []).length > 0 && (
        <div className="modal-section">
          <h3>NC Reason Breakdown</h3>
          <div className="mm-comp-reason-chart">
            {reasonBreakdown.map((r) => (
              <div key={r.reasonText} className="mm-comp-reason-row">
                <span className="mm-comp-reason-label" title={r.reasonText}>
                  {r.reasonText}
                </span>
                <div className="mm-comp-reason-bar-track">
                  <div
                    className="mm-comp-reason-bar-fill"
                    style={{
                      width: `${Math.max(4, (r.frequency / maxFreq) * 100)}%`,
                    }}
                  />
                </div>
                <span className="mm-comp-reason-count">{r.frequency}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NC event log */}
      <div className="modal-section modal-section--scroll">
        <h3>
          NC Event Log
          {(ncDetail ?? []).length > 0 && (
            <span className="mm-comp-section-count"> ({ncDetail.length})</span>
          )}
        </h3>
        {(ncDetail ?? []).length > 0 ? (
          <div className="modal-table-scroll">
            <table className="modal-table">
              <thead>
                <tr>
                  <Th
                    col="stationId"
                    sortCol={sortCol}
                    sortDir={sortDir}
                    onSort={onSort}
                  >
                    Station
                  </Th>
                  <th>Task</th>
                  <th>Step</th>
                  <th>Reason</th>
                  <th>Reported By</th>
                  <Th
                    col="updatedAt"
                    sortCol={sortCol}
                    sortDir={sortDir}
                    onSort={onSort}
                  >
                    Date
                  </Th>
                  <th>Alert</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((ev, i) => {
                  const alertLabel = ev.isAlertClosed
                    ? "Closed"
                    : ev.isAlertOpened
                      ? "Open"
                      : "None";
                  const alertVar = ev.isAlertClosed
                    ? "done"
                    : ev.isAlertOpened
                      ? "late"
                      : "neutral";
                  return (
                    <tr key={i}>
                      <td>{ev.zoneName ?? `Stn ${ev.stationId}`}</td>
                      <td title={ev.taskName}>{ev.taskName}</td>
                      <td>{ev.stepName ?? "—"}</td>
                      <td>{ev.reasonText ?? "—"}</td>
                      <td>{ev.updatedBy ?? "—"}</td>
                      <td>
                        {ev.updatedAt ? String(ev.updatedAt).slice(0, 10) : "—"}
                      </td>
                      <td>
                        <Tag label={alertLabel} variant={alertVar} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mm-comp-empty">
            No non-conformance events recorded for this module.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── PANEL ROUTER ─────────────────────────────────────────────────────────────

const SKELETON_CARDS = {
  peer: 5,
  project: 4,
  schedule: 4,
  quality: 4,
  jobs: 4,
};

// ─── ROOT COMPONENT ───────────────────────────────────────────────────────────

/**
 * ComparisonTool — rendered as the children of the shared Modal component.
 *
 * @param {{ module: object|null }} props
 *   module  The selected module from moduleMetricsPage.js.
 *           Required fields: workOrder, itemNumber, property, sqft,
 *           completionRate, currentStation.
 */
const ComparisonTool = ({ module }) => {
  const {
    activeTab,
    changeTab,
    tabData,
    retryTab,
    selectPeer,
    scheduleViewMode,
    setScheduleViewMode,
  } = useComparisonTool(module);

  if (!module) {
    return (
      <div className="mm-comp-content">
        <p className="modal-description">No module selected for comparison.</p>
      </div>
    );
  }

  // "Effective" tab — almost always activeTab, EXCEPT when the user is on
  // "schedule" with the job-completion slider active, in which case the data
  // being fetched/cached/shown lives in the hook's internal "jobs" slot
  // instead. See useComparisonTool.js's JOB-COMPLETION VIEW-MODE SLIDER note.
  const effectiveTab =
    activeTab === "schedule" && scheduleViewMode === "jobs"
      ? "jobs"
      : activeTab;

  const { status, data, error } = tabData[effectiveTab];

  // Map mode → panel component inline so we can pass selectPeer to HeadToHead.
  const renderPanel = () => {
    if (!data) return null;
    switch (effectiveTab) {
      case "peer":
        return <ItemNumberComparisonPanel data={data} module={module} />;
      case "project":
        return <ProjectProgressPanel data={data} module={module} />;
      case "schedule":
        return (
          <HeadToHeadPanel
            data={data}
            module={module}
            onSelectPeer={selectPeer}
          />
        );
      case "jobs":
        return <JobCompletionPanel data={data} module={module} />;
      case "quality":
        return <QualityPanel data={data} module={module} />;
      default:
        return null;
    }
  };

  return (
    <div className="mm-comp-content">
      {/* Persistent module summary header */}
      <div className="mm-comp-module-header">
        <div className="mm-comp-module-header-grid">
          {[
            { label: "Work Order", value: module.workOrder },
            { label: "Item Number", value: module.itemNumber },
            { label: "Property", value: module.property },
            { label: "Sqft", value: module.sqft?.toLocaleString() ?? "—" },
            {
              label: "Station",
              value:
                module.currentStation != null
                  ? `Stn ${module.currentStation}`
                  : "—",
            },
            {
              label: "Completion",
              value:
                module.completionRate != null
                  ? `${Number(module.completionRate).toFixed(1)}%`
                  : "—",
            },
          ].map(({ label, value }) => (
            <div key={label} className="mm-comp-module-header-item">
              <span className="mm-comp-module-header-label">{label}</span>
              <span className="mm-comp-module-header-value">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="mm-comp-tabs">
        {COMPARISON_TABS.map((tab) => (
          <button
            key={tab.id}
            className={`mm-comp-tab${activeTab === tab.id ? " mm-comp-tab--active" : ""}`}
            onClick={() => changeTab(tab.id)}
          >
            {tab.label}
            {tabData[tab.id].status === "success" && tab.id !== activeTab && (
              <span className="mm-comp-tab-dot" aria-hidden="true" />
            )}
          </button>
        ))}
      </div>

      {/* Metrics/Jobs slider + any-module search — only relevant to the Head
          to Head tab, but both stay visible regardless of which of its two
          views is currently loading, erroring, or showing data. The search
          bar calls the same selectPeer() the HeadToHeadPanel dropdown does,
          so it works whether the metrics or jobs view is currently active. */}
      {activeTab === "schedule" && (
        <div className="mm-comp-h2h-controls">
          <ViewModeSlider
            mode={scheduleViewMode}
            onChange={setScheduleViewMode}
          />
          <ModuleSearchBar
            excludeWoNum={module.workOrder}
            onSelect={selectPeer}
          />
        </div>
      )}

      {/* Tab panel content */}
      {/* The "jobs" mode can sit briefly in "idle" waiting for the metrics
          fetch to resolve a peer (see useComparisonTool.js) — treat that the
          same as "loading" so the slider doesn't flash blank content. */}
      {(status === "loading" ||
        (status === "idle" && effectiveTab === "jobs")) && (
        <LoadingSkeleton cards={SKELETON_CARDS[effectiveTab]} />
      )}

      {status === "error" && (
        <ErrorState error={error} onRetry={() => retryTab(effectiveTab)} />
      )}

      {status === "success" && renderPanel()}

      {/* "idle" is momentary for every other tab — the hook's effect fires
          before the next paint */}
    </div>
  );
};

export default ComparisonTool;
